/*
 * Document
 * @module components
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';

import { Content, Modal } from '~/components';

export type DocumentModalProps = {
  header?: React$Node,
  content?: React$Node,
  headerHtml?: string,
  contentHtml?: string,
  isVisible?: boolean,
  onClose?: () => void,
  device?: $PropertyType<DeviceQuery, 'device'>,
};

const DocumentModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const DocumentModalHeader = styled(Content.Header)`
  display: flex;
  position: initial;
  margin: -21px -15px 0;
  align-items: flex-start;
  border-radius: 6px 6px 0 0;
  flex: 0 0 auto;
  flex-direction: column;

  ${ifProp(
    { device: 'desktop' },
    css`
      padding: 35px 30px 25px;
    `,
    css`
      padding: 18px;
    `,
  )};
`;

const DocumentModalContent = styled.div`
  box-sizing: border-box;
  margin-top: 25px;
  padding: 0 16px;
  flex: 1;
  overflow: auto;
`;

/**
 * Document React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React$Node} [props.header] - header
 * @param {React$Node} [props.content] - content
 * @param {string} [props.headerHtml] - header html
 * @param {string} [props.contentHtml] - content html
 * @param {boolean} [props.isVisible] - modal visibility flag
 * @param {boolean} [props.isHtmlMarkup] - modal html markup flag
 * @param {Function} [props.onClose] - modal close handler
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @example <caption>Simple usage</caption>
 * <Document header={React$Node} content={React$Node} isVisible={true} />
 * @returns {React.Node<*>} React-component
 */
export const Document = ({
  header,
  content,
  headerHtml,
  contentHtml,
  isVisible,
  onClose,
  device,
}: DocumentModalProps) => (
  <Modal.Portal isVisible={isVisible} onClose={onClose} fullscreen>
    <DocumentModalWrapper testid="document" device={device}>
      {headerHtml ? (
        <DocumentModalHeader
          device={device}
          dangerouslySetInnerHTML={{ __html: headerHtml }}
        />
      ) : (
        <DocumentModalHeader device={device}>{header}</DocumentModalHeader>
      )}
      {contentHtml ? (
        <DocumentModalContent
          device={device}
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
      ) : (
        <DocumentModalContent device={device}>{content}</DocumentModalContent>
      )}
    </DocumentModalWrapper>
  </Modal.Portal>
);

Document.defaultProps = {
  onClose: () => {},
};
